import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Check, CreditCard, Calendar, Clock, ChevronLeft, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import { AddToCalendar } from "@/components/AddToCalendar";
import { SlotPicker } from "@/components/booking/SlotPicker";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STEPS = ["Select Time", "Review", "Payment", "Confirmation"];

export default function BookingFlow() {
  const { serviceId } = useParams();
  const [searchParams] = useSearchParams();
  const expertId = searchParams.get("expert");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [expert, setExpert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [notes, setNotes] = useState("");
  const [slotsData, setSlotsData] = useState(null);

  const fetchSlots = useCallback(async () => {
    try {
      const res = await fetch(`${API}/experts/${expertId}/slots?service_id=${serviceId}`, { credentials: "include" });
      if (res.ok) setSlotsData(await res.json());
      else setSlotsData({ has_availability: false, slots: [] });
    } catch {
      setSlotsData({ has_availability: false, slots: [] });
    }
  }, [expertId, serviceId]);

  useEffect(() => {
    if (expertId) fetchSlots();
  }, [expertId, fetchSlots]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/experts/${expertId}`);
        if (!res.ok) throw new Error("Expert not found");
        const data = await res.json();
        setExpert(data);
        const svc = data.services?.find((s) => s.service_id === serviceId);
        if (!svc) {
          toast.error("Service not found");
          navigate(`/expert/${expertId}`);
          return;
        }
        setService(svc);
      } finally {
        setLoading(false);
      }
    };
    if (expertId) fetchData();
  }, [serviceId, expertId]);

  const createBooking = async () => {
    setProcessing(true);
    try {
      const scheduledAt = selectedDate && selectedTime ? `${selectedDate}T${selectedTime}:00` : null;
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          council_member_id: expertId,
          service_id: serviceId,
          scheduled_at: scheduledAt,
          notes,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Booking failed");
      }
      const bkg = await res.json();
      setBooking(bkg);
      setStep(1);
    } catch (err) {
      toast.error(err.message);
      // Slot may have just been taken — refresh open slots
      if (slotsData?.has_availability) {
        setSelectedTime("");
        fetchSlots();
      }
    } finally {
      setProcessing(false);
    }
  };

  const initiatePayment = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/payments/create-order`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: booking.booking_id }),
      });
      const orderData = await res.json();
      setOrder(orderData);

      if (orderData.demo_mode) {
        // Demo mode: simulate payment
        toast.info("Demo mode: Simulating payment...");
        setTimeout(() => handleDemoPayment(orderData), 1500);
        return;
      }

      // Real Razorpay
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => openRazorpay(orderData);
      document.head.appendChild(script);
    } catch (err) {
      toast.error("Failed to initiate payment");
      setProcessing(false);
    }
  };

  const openRazorpay = (orderData) => {
    const options = {
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      order_id: orderData.order_id,
      name: "CouncilOne",
      description: service?.name,
      prefill: { name: user?.name, email: user?.email },
      theme: { color: "#0F172A" },
      handler: async (response) => {
        await verifyPayment(response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
      },
      modal: { ondismiss: () => { setProcessing(false); setStep(2); } },
    };
    new window.Razorpay(options).open();
    setStep(2);
    setProcessing(false);
  };

  const handleDemoPayment = async (orderData) => {
    await verifyPayment(orderData.order_id, "demo_payment_" + Date.now(), "demo_signature");
  };

  const verifyPayment = async (orderId, paymentId, signature) => {
    try {
      const res = await fetch(`${API}/payments/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          booking_id: booking.booking_id,
          razorpay_order_id: orderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
        }),
      });
      if (!res.ok) throw new Error("Payment verification failed");
      const data = await res.json();
      setConfirmation(data);
      setStep(3);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const confirmedBooking = useMemo(
    () => (booking ? { ...booking, google_meet_link: confirmation?.meet_link || booking.google_meet_link } : null),
    [booking, confirmation]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <div className="pt-20 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          {/* Back */}
          {step < 3 && (
            <button onClick={() => step > 0 ? setStep(step - 1) : navigate(`/expert/${expertId}`)} className="flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0F172A] transition-colors mb-6">
              <ChevronLeft size={16} /> {step === 0 ? "Back to profile" : "Back"}
            </button>
          )}

          {/* Progress */}
          <div className="flex items-center gap-0 mb-8">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${i < step ? "bg-[#064E3B] text-white" : i === step ? "bg-[#0F172A] text-white" : "bg-[#E4E4E1] text-[#A1A1AA]"}`}>
                    {i < step ? <Check size={14} /> : i + 1}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${i === step ? "text-[#0F172A]" : "text-[#A1A1AA]"}`}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-[#064E3B]" : "bg-[#E4E4E1]"}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Service summary */}
          <div className="card-flat mb-6">
            <div className="flex items-start gap-4">
              <img
                src={expert?.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(expert?.name || "")}&background=0F172A&color=fff`}
                alt={expert?.name}
                className="w-14 h-14 rounded-sm object-cover border border-[#E4E4E1]"
              />
              <div className="flex-1">
                <p className="font-semibold text-[#0F172A]">{service?.name}</p>
                <p className="text-sm text-[#52525B]">with {expert?.name}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-[#A1A1AA]">
                  <span className="flex items-center gap-1"><Clock size={11} /> {service?.duration} mins</span>
                  <span className="font-bold text-[#0F172A] text-sm">₹{service?.price?.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 0: Select time */}
          {step === 0 && (
            <div className="card-flat space-y-5">
              <h2 className="text-xl font-normal text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Schedule your session
              </h2>

              <SlotPicker
                slotsData={slotsData}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onSelectDate={(d) => { setSelectedDate(d); if (slotsData?.has_availability) setSelectedTime(""); }}
                onSelectTime={setSelectedTime}
                minDateStr={minDateStr}
              />
              <div>
                <label className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider block mb-2">Notes for the Expert (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="What would you like to discuss? Briefly describe your goals..."
                  className="w-full border border-[#E4E4E1] bg-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#0F172A] resize-none"
                  data-testid="booking-notes-input"
                />
              </div>
              <button
                onClick={createBooking}
                disabled={processing || (slotsData?.has_availability && (!selectedDate || !selectedTime))}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="continue-to-review-btn"
              >
                {processing ? "Creating booking..." : slotsData?.has_availability && (!selectedDate || !selectedTime) ? "Select a time slot to continue" : "Continue to Review"}
              </button>
            </div>
          )}

          {/* Step 1: Review */}
          {step === 1 && booking && (
            <div className="card-flat space-y-4">
              <h2 className="text-xl font-normal text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Review your booking
              </h2>
              <div className="space-y-3 text-sm">
                {[
                  { label: "Session", value: booking.service_name },
                  { label: "Expert", value: booking.expert_name },
                  { label: "Duration", value: `${booking.duration} minutes` },
                  { label: "Scheduled", value: booking.scheduled_at ? new Date(booking.scheduled_at).toLocaleString("en-IN") : "To be confirmed" },
                  { label: "Amount", value: `₹${booking.amount?.toLocaleString("en-IN")}` },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between border-b border-[#E4E4E1] pb-2">
                    <span className="text-[#52525B]">{item.label}</span>
                    <span className="font-medium text-[#0F172A]">{item.value}</span>
                  </div>
                ))}
                <div className="flex justify-between text-base font-bold pt-1">
                  <span className="text-[#0F172A]">Total</span>
                  <span className="text-[#0F172A]">₹{booking.amount?.toLocaleString("en-IN")}</span>
                </div>
              </div>
              <button
                onClick={initiatePayment}
                disabled={processing}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2"
                data-testid="proceed-to-payment-btn"
              >
                <CreditCard size={16} />
                {processing ? "Initiating payment..." : "Proceed to Payment"}
              </button>
              <p className="text-xs text-[#A1A1AA] text-center">Secure payment powered by Razorpay</p>
            </div>
          )}

          {/* Step 2: Payment processing */}
          {step === 2 && (
            <div className="card-flat text-center py-12">
              <div className="w-12 h-12 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#52525B] text-sm">Processing payment...</p>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && confirmation && (
            <div className="card-flat text-center py-10">
              <div className="w-16 h-16 bg-[#064E3B] rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={30} className="text-white" />
              </div>
              <h2 className="text-2xl font-normal text-[#0F172A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Booking Confirmed!
              </h2>
              <p className="text-[#52525B] text-sm mb-6">
                Your session with {expert?.name} has been confirmed.
              </p>
              {confirmation.meet_link && (
                <a
                  href={confirmation.meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 btn-primary text-sm mb-4"
                  data-testid="meet-link"
                >
                  <ExternalLink size={14} /> Join Google Meet
                </a>
              )}
              {booking?.scheduled_at && (
                <div className="flex justify-center mb-4">
                  <AddToCalendar booking={confirmedBooking} withName={expert?.name} />
                </div>
              )}
              <div className="flex gap-3 justify-center mt-4">
                <button onClick={() => navigate("/dashboard")} className="btn-secondary text-sm">
                  View My Bookings
                </button>
                <button onClick={() => navigate("/discover")} className="btn-primary text-sm">
                  Find More Experts
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
